import * as React from 'react';
import { useEffect, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ProductContext } from '../store/productContext';
import ProductListMobile from './ProductListMobile';
import loadingProducts from './loadingProducts';
import DesktopList from '../utils/components/DesktopList';
import ProductRow from './ProductRow';
import ProductForm from './ProductForm';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.5rem',
    },
  },
}));

const ProductList: React.FC = () => {
  const {
    getProducts,
    products,
    getProductsLoading,
    addProduct,
    addProductLoading,
  } = useContext(ProductContext);
  const theme = useTheme();
  const isMediumOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('products');

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  return (
    <Container maxWidth="lg">
      <Typography
        align="center"
        color="secondary"
        variant="h3"
        sx={(theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          marginBottom: theme.spacing(4),
          [theme.breakpoints.down('sm')]: {
            fontSize: '2.5rem',
          }
        })}>
        {t('products')}
      </Typography>
      {isMediumOrSmaller ? (
        <ProductListMobile
          products={getProductsLoading ? loadingProducts : products}
          loading={getProductsLoading}
        />
      ) : (
        <DesktopList<Product, ProductFormDto>
          headers={[
            'name',
            'code',
            'description',
            'dimensions',
            'unit',
            'productgroup',
          ]}
          data={getProductsLoading ? loadingProducts : products}
          loading={getProductsLoading}
          ns="products"
          add={addProduct}
          get={getProducts}
          emptyKey="noProducts"
          renderAddForm={({ onSubmit, buttonText }) => (
            <ProductForm
              loading={addProductLoading}
              onSubmit={onSubmit}
              buttonText={buttonText}
            />
          )}
          renderBody={({ data, loading, columnCount }) => (
            <ProductRow
              key={data.id}
              product={data}
              loading={loading}
              colCount={columnCount}
            />
          )}
          showAdd
        />
      )}
    </Container>
  );
};

export default ProductList;
